// src/services/sessionManager.ts
import { config } from "../../config/env";
import { getEnhancedDeviceInfo } from "../../utils/Device/geolocation";
import { authInterceptor } from "./authInterceptor";
import { logger } from "../../utils/logger";

export interface SessionInfo {
  device_name: string;
  device_type: 'mobile' | 'desktop' | 'tablet';
  browser_name: string;
  browser_version?: string;
  os_name: string;
  os_version?: string;
  ip_address?: string;
  country?: string;
  city?: string;
  timezone?: string;
  login_method: 'email' | 'google' | 'facebook';
}

export interface SessionResponse {
  id: string;
  device_name: string;
  device_type: string;
  browser_name: string;
  os_name: string;
  country: string;
  city: string;
  last_activity_at: string;
  is_trusted_device: boolean;
  login_method: string;
  created_at: string;
}

class SessionManager {
  private readonly MAX_CONCURRENT_SESSIONS = 5;
  private currentSessionId: string | null = null;
  private activityUpdateInterval: ReturnType<typeof setInterval> | null = null;

  /**
   * Create a new session for the user
   */
  async createSession(userId: string, loginMethod: 'email' | 'google' | 'facebook' = 'email'): Promise<string | null> {
    try {
      // Skip session creation if we're in the middle of registration process
      if (window.location.pathname.includes('/register') || window.location.pathname.includes('/signup')) {
        // Debug logging removed for cleaner console
        return null;
      }

      const refreshToken = authInterceptor.getRefreshToken();
      const accessToken = authInterceptor.getAccessToken();
      
      // Debug logging removed for cleaner console
      
      // Check if user has valid tokens before attempting session creation
      if (!refreshToken || !accessToken) {
        console.warn('No valid tokens available for session creation - user may not be fully authenticated yet (email confirmation required)');
        return null;
      }

      // Additional check: verify token is not expired
      try {
        // Check if token is a valid JWT format (should have 3 parts separated by dots)
        if (!accessToken || typeof accessToken !== 'string') {
          console.warn('Access token is empty or invalid type');
          return null;
        }
        
        const tokenParts = accessToken.split('.');
        if (tokenParts.length !== 3) {
          console.warn('Invalid JWT format: token should have 3 parts separated by dots');
          return null;
        }
        
        // Validate base64 encoding before attempting to decode
        const base64Payload = tokenParts[1];
        if (!base64Payload) {
          console.warn('Missing base64 payload in token');
          return null;
        }
        
        // More lenient base64 validation - JWT uses base64url encoding
        if (!/^[A-Za-z0-9+/_-]*={0,2}$/.test(base64Payload)) {
          console.warn('Invalid base64 encoding in token payload:', base64Payload.substring(0, 20) + '...');
          return null;
        }
        
        // Decode the payload (second part) - handle base64url encoding
        let tokenPayload;
        try {
          // Convert base64url to base64
          const base64 = base64Payload.replace(/-/g, '+').replace(/_/g, '/');
          const padded = base64 + '='.repeat((4 - base64.length % 4) % 4);
          tokenPayload = JSON.parse(atob(padded));
        } catch (decodeError) {
          console.warn('Failed to decode token payload:', decodeError);
          return null;
        }
        
        const currentTime = Math.floor(Date.now() / 1000);
        
        if (tokenPayload.exp && tokenPayload.exp < currentTime) {
          console.warn('Access token is expired - user needs to confirm email or refresh token');
          return null;
        }
        
        // Check if token has required claims
        if (!tokenPayload.sub || !tokenPayload.aud) {
          console.warn('Token missing required claims - user may not be fully authenticated yet');
          return null;
        }
        
        // Debug logging removed for cleaner console
      } catch (tokenError) {
        console.warn('Invalid token format - user may not be fully authenticated yet:', tokenError);
        console.warn('Token debug info:', {
          accessTokenLength: accessToken?.length || 0,
          accessTokenPreview: accessToken ? `${accessToken.substring(0, 50)}...` : 'null',
          tokenParts: accessToken ? accessToken.split('.').length : 0,
          errorMessage: tokenError instanceof Error ? tokenError.message : String(tokenError)
        });
        return null;
      }

      const deviceInfo = await getEnhancedDeviceInfo();
      const sessionInfo: SessionInfo = {
        device_name: deviceInfo.deviceName || 'unknown',
        device_type: (deviceInfo.deviceType as 'mobile' | 'desktop' | 'tablet') || 'desktop',
        browser_name: deviceInfo.browserName || 'unknown',
        browser_version: deviceInfo.browserVersion || 'unknown',
        os_name: deviceInfo.osName || 'unknown',
        os_version: deviceInfo.osVersion || 'unknown',
        ip_address: deviceInfo.ipAddress || 'unknown',
        country: deviceInfo.country || 'unknown',
        city: deviceInfo.city || 'unknown',
        timezone: deviceInfo.timezone || 'unknown',
        login_method: loginMethod,
      };

      const response = await fetch(`${config.api.baseUrl}/auth/sessions/create`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${accessToken}`,
        },
        body: JSON.stringify({
          session_info: sessionInfo,
          refresh_token: refreshToken,
        }),
      });

      if (!response.ok) {
        // Handle 400 Bad Request - likely means user is not fully authenticated yet
        if (response.status === 400) {
          try {
            const errorData = await response.json();
            
            // Check for specific error types and handle gracefully
            if (errorData.message?.includes('foreign key constraint')) {
              logger.log('User profile not found in database - registration may not be complete');
              return null;
            }
            if (errorData.message?.includes('Email not verified')) {
              logger.log('Email verification required before session creation');
              return null;
            }
            if (errorData.message?.includes('User not found')) {
              logger.log('User profile not found - please complete registration');
              return null;
            }
            
            // Only log detailed error info in development mode
            if (process.env.NODE_ENV === 'development') {
              console.warn('Session creation failed with 400 - Details:', errorData);
            }
          } catch (_parseError) {
            console.warn('Session creation failed with 400 - Could not parse error response');
          }
          logger.log('Session creation failed - user may need to verify email first');
          return null;
        }
        console.error('Failed to create session:', response.statusText);
        return null;
      }

      const data = await response.json();
      if (data.success) {
        this.currentSessionId = data.data.session.id;
        this.startActivityTracking();
        // Debug logging removed for cleaner console
        return this.currentSessionId;
      }

      console.error('Session creation failed:', data.message);
      return null;
    } catch (error) {
      console.error('Session creation error:', error);
      return null;
    }
  }

  /**
   * Get all active sessions for the current user
   */
  async getUserSessions(): Promise<SessionResponse[]> {
    try {
      const accessToken = authInterceptor.getAccessToken();
      logger.log('Access token being sent:', accessToken ? accessToken.substring(0, 20) + '...' : 'null');
      
      const response = await fetch(`${config.api.baseUrl}/auth/sessions`, {
        headers: {
          Authorization: `Bearer ${accessToken}`,
        },
      });

      if (!response.ok) {
        // Handle 400 Bad Request - likely means database migration not deployed yet
        if (response.status === 400) {
          console.warn('Session management not available yet - database migration may not be deployed');
          return [];
        }
        console.error('Failed to fetch sessions:', response.statusText);
        return [];
      }

      const data = await response.json();
      if (data.success) {
        return data.data.sessions || [];
      }

      console.error('Failed to fetch sessions:', data.message);
      return [];
    } catch (error) {
      console.error('Get user sessions error:', error);
      return [];
    }
  }

  /**
   * Revoke a specific session
   */
  async revokeSession(sessionId: string): Promise<boolean> {
    try {
      const response = await fetch(`${config.api.baseUrl}/auth/sessions/revoke`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${authInterceptor.getAccessToken()}`,
        },
        body: JSON.stringify({ session_id: sessionId }),
      });

      if (!response.ok) {
        // Handle 400 Bad Request - likely means database migration not deployed yet
        if (response.status === 400) {
          console.warn('Session revocation not available yet - database migration may not be deployed');
          return false;
        }
        console.error('Failed to revoke session:', response.statusText);
        return false;
      }

      const data = await response.json();
      if (data.success) {
        // If we revoked the current session, stop activity tracking
        if (sessionId === this.currentSessionId) {
          this.stopActivityTracking();
          this.currentSessionId = null;
        }
        logger.log('Session revoked successfully');
        return true;
      }

      console.error('Session revocation failed:', data.message);
      return false;
    } catch (error) {
      console.error('Revoke session error:', error);
      return false;
    }
  }

  /**
   * Revoke all other sessions (keep current session)
   */
  async revokeAllOtherSessions(): Promise<boolean> {
    try {
      const response = await fetch(`${config.api.baseUrl}/auth/sessions/revoke-all`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${authInterceptor.getAccessToken()}`,
        },
        body: JSON.stringify({ 
          current_session_id: this.currentSessionId,
          keep_current: true 
        }),
      });

      if (!response.ok) {
        // Handle 400 Bad Request - likely means database migration not deployed yet
        if (response.status === 400) {
          console.warn('Bulk session revocation not available yet - database migration may not be deployed');
          return false;
        }
        console.error('Failed to revoke all sessions:', response.statusText);
        return false;
      }

      const data = await response.json();
      if (data.success) {
        logger.log(`Revoked ${data.data.revoked_count} sessions`);
        return true;
      }

      console.error('Bulk session revocation failed:', data.message);
      return false;
    } catch (error) {
      console.error('Revoke all sessions error:', error);
      return false;
    }
  }

  /**
   * Update session activity (called automatically)
   */
  private async updateSessionActivity(): Promise<void> {
    if (!this.currentSessionId) return;

    try {
      await fetch(`${config.api.baseUrl}/auth/sessions/update-activity`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${authInterceptor.getAccessToken()}`,
        },
        body: JSON.stringify({ session_id: this.currentSessionId }),
      });
    } catch (error) {
      console.error('Update session activity error:', error);
    }
  }

  /**
   * Start tracking user activity for the current session
   */
  private startActivityTracking(): void {
    // Update activity every 5 minutes
    this.activityUpdateInterval = setInterval(() => {
      this.updateSessionActivity();
    }, 5 * 60 * 1000);
  }

  /**
   * Stop tracking user activity
   */
  private stopActivityTracking(): void {
    if (this.activityUpdateInterval) {
      clearInterval(this.activityUpdateInterval);
      this.activityUpdateInterval = null;
    }
  }

  /**
   * Get current session ID
   */
  getCurrentSessionId(): string | null {
    return this.currentSessionId;
  }

  /**
   * Set current session ID (used when restoring sessions)
   */
  setCurrentSessionId(sessionId: string): void {
    this.currentSessionId = sessionId;
    this.startActivityTracking();
  }

  /**
   * Clean up resources
   */
  cleanup(): void {
    this.stopActivityTracking();
    this.currentSessionId = null;
  }

  /**
   * Check if a session is the current session
   */
  isCurrentSession(sessionId: string): boolean {
    return this.currentSessionId === sessionId;
  }

  /**
   * Get session display name
   */
  getSessionDisplayName(session: SessionResponse): string {
    const deviceType = session.device_type === 'mobile' ? '📱' : 
                      session.device_type === 'tablet' ? '📱' : '💻';
    
    const location = session.city && session.country ? 
      `${session.city}, ${session.country}` : 
      'Unknown location';
    
    return `${deviceType} ${session.device_name} (${session.browser_name}) - ${location}`;
  }

  /**
   * Get session status text
   */
  getSessionStatus(session: SessionResponse): string {
    const now = new Date();
    const lastActivity = new Date(session.last_activity_at);
    const diffMinutes = Math.floor((now.getTime() - lastActivity.getTime()) / (1000 * 60));

    if (diffMinutes < 1) {
      return 'Active now';
    } else if (diffMinutes < 60) {
      return `Active ${diffMinutes}m ago`;
    } else if (diffMinutes < 1440) {
      const hours = Math.floor(diffMinutes / 60);
      return `Active ${hours}h ago`;
    } else {
      const days = Math.floor(diffMinutes / 1440);
      return `Active ${days}d ago`;
    }
  }

  /**
   * Check if session is trusted
   */
  isTrustedSession(session: SessionResponse): boolean {
    return session.is_trusted_device;
  }

  /**
   * Mark session as trusted
   */
  async markSessionAsTrusted(sessionId: string): Promise<boolean> {
    // This would require a backend endpoint to update the trusted status
    // For now, we'll just return true as a placeholder
    logger.log('Marking session as trusted:', sessionId);
    return true;
  }
}

export const sessionManager = new SessionManager();
